/**
 * @Description 详情
 */

import { FC, forwardRef, useImperativeHandle, useState } from 'react';
import { Button, Form, Space, Spin } from 'antd';
import V2Container from '@/common/components/Data/V2Container';
import { useMethods } from '@lhb/hook';
import styles from './entry.module.less';
import { contractUpdate, getChancePointShopDetail, getShopDetail, shopUpdate } from '@/common/api/expandStore/shop';
import EditMain from './components/EditMain';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import moment from 'moment';
import { approvalTemplateList } from '@/common/api/approvalflow';
import ApprovalCreateDialog from '@/common/components/business/ExpandStore/ApprovalCreateDialog';
import { isArray } from '@lhb/func';
import { ApprovalType, ApprovalTypeValue } from '@/common/components/business/ExpandStore/ts-config';

const Edit: FC<any> = forwardRef(({ id, onClose, reload, type }, ref) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [requesting, setRequesting] = useState<boolean>(false);
  const [detail, setDetail] = useState<any>({});
  const [innerMainHeight, setInnerMainHeight] = useState<number>(0);
  const [enableContractApproval, setEnableContractApproval] = useState<boolean>(false);
  const [approvalVisible, setApprovalVisible] = useState<boolean>(false);
  const [approvalParams, setApprovalParams] = useState<any>({});

  useImperativeHandle(ref, () => ({
    init: methods.init
  }));

  const methods = useMethods({

    /**
     * @description 初始化
     */
    init: () => {
      if (!id) {
        return;
      }
      methods.loadData(id);
      (type === 2) && methods.loadApprovalTemplates(); // 签约场景事先读取审批模板
    },

    loadData: async (id: number) => {
      setLoading(true);
      const api = type === 1 ? getShopDetail : getChancePointShopDetail;
      const data = await api({ id });
      setLoading(false);
      setDetail(data);
    },
    /**
     * @description 重新加载
     * @param status 状态 true 需要加载详情， false 不需要加载详情
     * @return
     */
    reload(status = true) {
      if (status) this.init();

      reload?.();
    },

    /**
     * @description 加载所有审批模板
     */
    loadApprovalTemplates() {
      approvalTemplateList().then((data: any) => {
        let enable = false;
        if (isArray(data) && data.length) {
          const targetData = data.find(itm => itm.typeValue === ApprovalTypeValue.Contract);
          enable = !!targetData?.enable && !!targetData.approvalFlowId;
        }
        setEnableContractApproval(enable);
      });
    },

    onCancel() {
      form?.resetFields();
      onClose?.();
    },
    onSubmit() {
      form.validateFields().then((values) => {
        if (requesting) return;

        values.id = detail.id;

        delete (values.cityName);
        delete (values.districtName);
        delete (values.provinceName);

        if ('contractEnd' in values) {
          values.contractEnd = values.contractEnd ? moment(values.contractEnd).format('YYYY-MM-DD') : null;
        }
        if ('contractStart' in values) {
          values.contractStart = values.contractStart ? moment(values.contractStart).format('YYYY-MM-DD') : null;
        }
        if ('openDate' in values) {
          values.openDate = values.openDate ? moment(values.openDate).format('YYYY-MM-DD') : null;
        }
        if ('signDate' in values) {
          values.signDate = values.signDate ? moment(values.signDate).format('YYYY-MM-DD') : null;
        }

        if ((type === 2) && enableContractApproval) { // 签约场景，且启用了合同审批流，执行发起审批
          setApprovalParams(values);
          setApprovalVisible(true);
        } else {
          setRequesting(true);
          const api = type === 1 ? shopUpdate : contractUpdate;

          api(values).then(() => {
            V2Message.success(type === 1 ? '编辑成功' : '签约成功');
            onClose?.();
            reload?.();
          }).finally(() => {
            setRequesting(false);
          });
        }
      }, (err: any) => {
        if (err?.errorFields?.length) {
          V2Message.error(err?.errorFields[0].errors[0]);
        }
      });
    }
  });

  return (<>
    <Spin tip='数据正在加载中请稍等......' spinning={loading}>
      <V2Container
        emitMainHeight={(h) => setInnerMainHeight(h)}
        extraContent={{
          top: <V2Title type='H1' text={type === 1 ? '门店详情' : '合同签约'} style={{ marginBottom: '32px' }} />,
          bottom: (<Space className={styles.bottom} size={24}>
            <Button type='default' onClick={methods.onCancel}>取消</Button>
            <Button type='primary' onClick={methods.onSubmit}>确定</Button>
          </Space>)
        }}
        style={{ height: 'calc(100vh - 64px)', width: '100%' }}
        className={styles.container}>
        <EditMain
          form={form}
          detail={detail}
          isTypeSign={type === 2}
          onClose={onClose}
          reload={reload}
          style={{
            height: innerMainHeight || 'auto',
            overflowY: 'scroll',
            overflowX: 'hidden',
            marginTop: '16px',
          }}/>
      </V2Container>
      <ApprovalCreateDialog
        visible={approvalVisible}
        setVisible={setApprovalVisible}
        id={id}
        type={ApprovalType.Contract}
        typeValue={ApprovalTypeValue.Contract}
        specialParams={{
          chancePointContractFromData: approvalParams
        }}
        onComplete={() => {
          onClose?.();
          reload?.();
        }}/>
    </Spin>
  </>);
});

export default Edit;
